import React, {useCallback, useEffect, useState} from 'react';
import {
    Alert,
    Box,
    Fab,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography
} from '@mui/material';
import PromptTableItem from './PromptTableItem';
import {usePrompts} from "../hooks/usePrompts";
import Loader from "./Loader";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import AddPrompt from "./AddPrompt";

const PromptsTab = ({company, onPromptSelect}) => {
    const [currentTab, setCurrentTab] = useState(0);
    const {prompts, loading, error, fetchPrompts} = usePrompts();
    const [addPrompt, setAddPrompt] = useState(false);

    // Filter states
    const [filters, setFilters] = useState({
        keyword: '',
        type: '',
        nonSystemOnly: false,
        format: null
    });

    const [availableTypes, setAvailableTypes] = useState([]);
    const [availableFormats, setAvailableFormats] = useState([]);
    const [searchTimeout, setSearchTimeout] = useState(null);

    // Map tab indices to category objects with display name and API value
    const tabCategories = [
        {display: "Articles", value: "ARTICLES"},
        {display: "Single Post", value: "SINGLE_POST"},
        {display: "Article Titles", value: "ARTICLE_TITLES"},
        {display: "Article Taglines", value: "ARTICLE_TAGLINES"},
        {display: "Banner Titles", value: "BANNER_TITLES"},
        {display: "News", value: "NEWS"},
        {display: "Weekly News", value: "WEEKLY_NEWS"}
    ];

    // Available prompt types (you can fetch this from an API or define statically)
    const promptTypes = [
        {display: "Format", value: "FORMAT"},
        {display: "Sub Format", value: "SUB_FORMAT"},
        {display: "Prompt", value: "PROMPT"},
    ];

    const category = tabCategories[currentTab].value;

    const handleClose = () => {
        setAddPrompt(false);
    };

    const handleTabChange = (event, val) => {
        setCurrentTab(val);
        // Reset filters when changing tabs
        setFilters(prev => ({
            ...prev,
            keyword: '',
            type: '',
            format: null // Also reset format when changing tabs
        }));
    };

    const handleFilterChange = (filterName, value) => {
        setFilters(prev => ({
            ...prev,
            [filterName]: value
        }));
    };

    const handleKeywordChange = (value) => {
        // Clear existing timeout
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        // Update keyword immediately for UI responsiveness
        setFilters(prev => ({
            ...prev,
            keyword: value
        }));

        // Set new timeout for API call
        const timeout = setTimeout(() => {
            // The actual fetch will be triggered by useEffect
        }, 500); // 500ms debounce

        setSearchTimeout(timeout);
    };

    const clearFilters = () => {
        setFilters({
            keyword: '',
            type: '',
            nonSystemOnly: false,
            format: null
        });
    };

    const hasActiveFilters = filters.keyword || filters.type || filters.nonSystemOnly || filters.format;

    // Extract available formats from current prompts
    useEffect(() => {
        if (prompts && prompts.length > 0) {
            // Get unique formats from the current prompt results
            const formats = prompts
                .filter(prompt => prompt.formatId && prompt.formatName) // Only include prompts with valid format data
                .reduce((unique, prompt) => {
                    // Remove duplicates based on format id
                    if (!unique.find(f => f.formatId === prompt.formatId)) {
                        unique.push({
                            formatId: prompt.formatId,
                            formatName: prompt.formatName
                        });
                    }
                    return unique;
                }, [])
                .sort((a, b) => a.formatName.localeCompare(b.formatName)); // Sort alphabetically by name

            setAvailableFormats(formats);

            // If current selected format is not in the available formats, clear it
            if (filters.format && !formats.find(f => f.formatId === filters.format.formatId)) {
                setFilters(prev => ({
                    ...prev,
                    format: null
                }));
            }
        } else {
            // No prompts available, clear formats
            setAvailableFormats([]);
            if (filters.format) {
                setFilters(prev => ({
                    ...prev,
                    format: null
                }));
            }
        }
    }, [prompts, filters.format]);

    useEffect(() => {
        const filterParams = {
            companyId: company?.id,
            category: category,
            ...(filters.keyword && {keyword: filters.keyword}),
            ...(filters.type && {type: filters.type}),
            nonSystemOnly: company?.id != null,
            formatId: filters.format?.formatId || null
        };

        fetchPrompts(filterParams);
    }, [company?.id, category, filters, fetchPrompts]);

    // Load available types for the current category
    useEffect(() => {
        if (category) {
            // You might want to fetch available types from your backend
            setAvailableTypes(promptTypes);
        }
    }, [company?.id, category]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
        };
    }, [searchTimeout]);

    return (
        <Box>
            <AddPrompt company={company} open={addPrompt} handleClose={handleClose}/>

            <Fab
                sx={{
                    position: 'fixed',
                    top: 'auto',
                    right: 20,
                    bottom: 20,
                    left: 'auto',
                }}
                color="primary"
                aria-label="add"
                onClick={() => setAddPrompt(true)}
            >
                <AddIcon/>
            </Fab>

            {/* Category Tabs */}
            <Box sx={{display: "flex", alignItems: "center", justifyContent: "center", mb: 3}}>
                <Tabs value={currentTab} onChange={handleTabChange} aria-label="prompt categories">
                    {tabCategories.map((category, index) => (
                        <Tab
                            key={index}
                            sx={{textTransform: "none"}}
                            label={category.display}
                        />
                    ))}
                </Tabs>
            </Box>

            {/* Filters Section */}
            <Paper elevation={2} sx={{p: 3, mb: 3}}>
                <Typography variant="h6" gutterBottom>
                    Filters
                    {hasActiveFilters && (
                        <IconButton
                            size="small"
                            onClick={clearFilters}
                            sx={{ml: 1}}
                            title="Clear all filters"
                        >
                            <ClearIcon/>
                        </IconButton>
                    )}
                </Typography>

                <Grid container spacing={2} alignItems="center">
                    {/* Keyword Search */}
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            fullWidth
                            label="Search Keywords"
                            variant="outlined"
                            value={filters.keyword}
                            onChange={(e) => handleKeywordChange(e.target.value)}
                            InputProps={{
                                startAdornment: <SearchIcon color="action" sx={{mr: 1}}/>,
                                endAdornment: filters.keyword && (
                                    <IconButton
                                        size="small"
                                        onClick={() => handleKeywordChange('')}
                                    >
                                        <ClearIcon/>
                                    </IconButton>
                                )
                            }}
                            placeholder="Search by name or description..."
                        />
                    </Grid>

                    {/* Type Filter */}
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                            <InputLabel>Type</InputLabel>
                            <Select
                                value={filters.type}
                                label="Type"
                                onChange={(e) => handleFilterChange('type', e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>All Types</em>
                                </MenuItem>
                                {availableTypes.map((type) => (
                                    <MenuItem key={type.value} value={type.value}>
                                        {type.display}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Format Filter - Only show when type is SUB_FORMAT */}
                    {filters.type === "SUB_FORMAT" && (
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>Format</InputLabel>
                                <Select
                                    value={filters.format?.formatId || ''}
                                    label="Format"
                                    onChange={(e) => {
                                        const selectedFormat = e.target.value
                                            ? availableFormats.find(f => f.formatId === e.target.value)
                                            : null;
                                        handleFilterChange('format', selectedFormat);
                                    }}
                                    disabled={availableFormats.length === 0}
                                >
                                    <MenuItem value="">
                                        <em>All Formats</em>
                                    </MenuItem>
                                    {availableFormats.map((format) => (
                                        <MenuItem key={format.formatId} value={format.formatId}>
                                            {format.formatName}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {availableFormats.length === 0 && (
                                    <Typography variant="caption" color="textSecondary" sx={{mt: 0.5}}>
                                        No formats available for current filters
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>
                    )}

                    {/* Results Count */}
                    <Grid item xs={12} sm={6} md={2}>
                        <Typography variant="body2" color="textSecondary">
                            {prompts ? `${prompts.length} results` : ''}
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{mb: 2}}>
                    {error}
                </Alert>
            )}

            {/* Loading Indicator */}
            <Loader loading={loading}/>

            {/* Results Table */}
            <Table sx={{
                marginTop: "25px",
                "&.MuiTable-root": {
                    borderCollapse: "separate",
                    borderSpacing: "0 15px",
                }
            }}>
                <TableHead>
                    <TableRow>
                        <TableCell align="left">Name</TableCell>
                        <TableCell align="center">Category</TableCell>
                        <TableCell align="center">Type</TableCell>
                        <TableCell align="center">Format Name</TableCell>
                        <TableCell align="center">Create Date</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {prompts && prompts.length > 0 ? (
                        prompts.map((prompt, i) => (
                            <PromptTableItem
                                item={prompt}
                                key={i}
                                onSelect={() => onPromptSelect(prompt)}
                            />
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={6} align="center">
                                {hasActiveFilters
                                    ? "No prompts found matching the current filters"
                                    : "No prompts found for this category"
                                }
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </Box>
    );
};

export default PromptsTab;